import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Plus, Target, Users } from "lucide-react";

type Mission = {
  id: string; title: string; description: string | null; mission_type: string;
  reward_type: string; reward_amount_cents: number; reward_points: number; reward_badge: string | null;
  status: string; deadline: string | null; participant_cap: number | null; creator_id: string;
};

const TYPES = ["repost", "invite", "attend_event", "qr_scan", "buy_merch", "location_hunt", "social_task", "livestream"];

export default function CreatorMissions() {
  const { user } = useAuth();
  const [missions, setMissions] = useState<Mission[]>([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [participation, setParticipation] = useState<Record<string, string>>({});
  const [form, setForm] = useState({
    title: "", description: "", mission_type: "repost",
    reward_type: "points", reward_points: "100", reward_amount_cents: "0",
    reward_badge: "", deadline: "", participant_cap: "",
  });

  const load = async () => {
    const { data } = await supabase.from("creator_missions").select("*").eq("status", "open").order("created_at", { ascending: false });
    setMissions((data as Mission[]) || []);
    if (user) {
      const { data: parts } = await supabase.from("mission_participants").select("mission_id, status").eq("fan_id", user.id);
      const map: Record<string, string> = {};
      (parts as any[] | null)?.forEach((p) => { map[p.mission_id] = p.status; });
      setParticipation(map);
    }
  };

  useEffect(() => { load(); }, [user]);

  const submit = async () => {
    if (!user) return;
    if (!form.title.trim()) { toast.error("Title required"); return; }
    setBusy(true);
    const { error } = await supabase.from("creator_missions").insert({
      creator_id: user.id,
      title: form.title.trim(),
      description: form.description.trim() || null,
      mission_type: form.mission_type,
      reward_type: form.reward_type,
      reward_points: parseInt(form.reward_points || "0") || 0,
      reward_amount_cents: Math.round(parseFloat(form.reward_amount_cents || "0") * 100),
      reward_badge: form.reward_badge.trim() || null,
      deadline: form.deadline ? new Date(form.deadline).toISOString() : null,
      participant_cap: form.participant_cap ? parseInt(form.participant_cap) : null,
    });
    setBusy(false);
    if (error) { toast.error(error.message); return; }
    toast.success("Mission launched");
    setOpen(false);
    setForm({ ...form, title: "", description: "" });
    load();
  };

  const join = async (missionId: string) => {
    if (!user) return;
    const { error } = await supabase.from("mission_participants").insert({ mission_id: missionId, fan_id: user.id });
    if (error) { toast.error(error.message); return; }
    toast.success("Joined mission");
    load();
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="sticky top-0 z-10 backdrop-blur-md bg-background/80 border-b">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">
          <Button asChild size="icon" variant="ghost"><Link to="/creator-mode"><ArrowLeft className="size-5" /></Link></Button>
          <h1 className="text-xl font-bold flex-1">Fan Missions</h1>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button size="sm"><Plus className="size-4 mr-1" />New</Button></DialogTrigger>
            <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Launch a mission</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="space-y-2"><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} maxLength={120} /></div>
                <div className="space-y-2"><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} maxLength={500} /></div>
                <div className="space-y-2"><Label>Type</Label>
                  <Select value={form.mission_type} onValueChange={(v) => setForm({ ...form, mission_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t} className="capitalize">{t.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-2"><Label>Reward type</Label>
                    <Select value={form.reward_type} onValueChange={(v) => setForm({ ...form, reward_type: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="points">Points</SelectItem>
                        <SelectItem value="cash">Cash</SelectItem>
                        <SelectItem value="badge">Badge</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {form.reward_type === "points" && <div className="space-y-2"><Label>Points</Label><Input type="number" value={form.reward_points} onChange={(e) => setForm({ ...form, reward_points: e.target.value })} /></div>}
                  {form.reward_type === "cash" && <div className="space-y-2"><Label>USD</Label><Input type="number" value={form.reward_amount_cents} onChange={(e) => setForm({ ...form, reward_amount_cents: e.target.value })} /></div>}
                  {form.reward_type === "badge" && <div className="space-y-2"><Label>Badge name</Label><Input value={form.reward_badge} onChange={(e) => setForm({ ...form, reward_badge: e.target.value })} /></div>}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-2"><Label>Deadline</Label><Input type="date" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} /></div>
                  <div className="space-y-2"><Label>Cap</Label><Input type="number" value={form.participant_cap} onChange={(e) => setForm({ ...form, participant_cap: e.target.value })} /></div>
                </div>
                <Button onClick={submit} disabled={busy} className="w-full">{busy ? "Launching…" : "Launch"}</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </header>
      <main className="max-w-2xl mx-auto px-4 py-6 space-y-3">
        {missions.length === 0 ? (
          <Card><CardContent className="py-12 text-center text-muted-foreground"><Target className="size-10 mx-auto mb-2" />No open missions yet.</CardContent></Card>
        ) : missions.map((m) => {
          const mine = m.creator_id === user?.id;
          const joined = participation[m.id];
          return (
            <Card key={m.id}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-2">
                  <CardTitle className="text-base">{m.title}</CardTitle>
                  <Badge variant="outline" className="capitalize">{m.mission_type.replace(/_/g, " ")}</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {m.description && <p className="text-sm text-muted-foreground">{m.description}</p>}
                <div className="flex items-center justify-between text-sm">
                  <span className="font-semibold">
                    {m.reward_type === "cash" ? `$${(m.reward_amount_cents / 100).toFixed(2)}` :
                     m.reward_type === "badge" ? `🏅 ${m.reward_badge}` : `${m.reward_points} pts`}
                  </span>
                  {!mine && (joined ? (
                    <Badge variant="secondary" className="capitalize">{joined}</Badge>
                  ) : (
                    <Button size="sm" onClick={() => join(m.id)}><Users className="size-3.5 mr-1" />Join</Button>
                  ))}
                  {mine && <Badge>Yours</Badge>}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </main>
    </div>
  );
}
